import { inject } from 'inversify';

import type { ITrip } from '../../types/api/ITrip';

import {
  TRIPS,
} from '@/constants';
import { UserController } from '@/controllers/UserController';
import { service } from '@/decorators/service';
import { SignalrListener, SignalrMessage } from '@/decorators/signalr';
import { Alert } from '@/entities/Alert';
// import CONSTANTS from '@/helpers/constants';
import { Logger } from '@/services/Logger';
import { AlertUpdateReqMsg } from '@/services/signalr/MessageTypes/Alert';
import { Signalr } from '@/services/signalr/Signalr';
import { AlertStore } from '@/stores/AlertStore';
import { findLeg } from '@/utils/findLeg';
import { getAlertAddress } from '@/utils/getAlertAddress';
import { orderByField } from '@/utils/orderBy';
import { IAlertInfo } from '~/api/IAlertInfo';
import { IAlertUpdateRespMsg } from '~/api/messages/IAlertUpdateRespMsg';
import { IAlertsRespMsg } from '~/api/messages/IAlertsRespMsg';
import { IDispatchMessage } from '~/api/messages/IDispatchMessage';

@SignalrListener
@service(AlertManager)
export class AlertManager {
  @inject(Logger) protected logger: Logger;
  @inject(Signalr) protected signalr: Signalr;
  @inject(UserController) protected userController: UserController;
  @inject(AlertStore) protected alertStore: AlertStore;

  @SignalrMessage('AlertsRespMsg')
  async onAlertsRespMsg(response: IAlertsRespMsg & IDispatchMessage) {
    if (response.alerts) {
      await this.update([...response.alerts]);
    }
  }

  @SignalrMessage('AlertUpdateRespMsg')
  async onAlertUpdateRespMsg(response: IAlertUpdateRespMsg & IDispatchMessage) {
    await this.update([response.alert]);
  }

  getAlerts(address: string) {
    const alerts = this.alertStore.all()
      ?.filter((item) => (item.address === address && item.status === '1' && item.patientId === ''));
    return orderByField(alerts, this.alertStore.valueKey);
  }

  getPatientAlerts(patientId: string) {
    const alerts = this.alertStore.all()
      ?.filter((item) => (item.patientId === patientId && item.status === '1'));
    return orderByField(alerts, this.alertStore.valueKey);
  }

  getPickupAlerts = (trip: ITrip) => {
    const pickUpLeg = findLeg(trip.legs, TRIPS.CONTEXT.PICK_UP);
    return this.getAlerts(getAlertAddress(pickUpLeg));
  };

  getDropoffAlerts = (trip: ITrip) => {
    const dropOffLeg = findLeg(trip.legs, TRIPS.CONTEXT.DROP_OFF);
    return this.getAlerts(getAlertAddress(dropOffLeg));
  };

  async addAlert(alert: IAlertInfo) {
    const normalized = new Alert({ ...alert });
    await this.signalr.invoke(AlertUpdateReqMsg, {
      alert: normalized,
    });
  }

  async update(alerts: IAlertInfo[]) {
    this.alertStore.batchUpdate(alerts);
  }
}
